import type { DebugBridge } from './DebugBridge';

export interface CodeBreakpoint {
  address: number;
  csr: number;
  enabled: boolean;
  condition?: string;
  hitCount: number;
}

export interface MemoryBreakpoint {
  address: number;
  length: number;
  type: 'read' | 'write' | 'both';
  enabled: boolean;
  hitCount: number;
}

function parseCondition(condition: string, registers: Record<string, number>): boolean {
  const m = condition.match(/^\s*(R\d+|ER\d+|PC|PSW|CSR|SP)\s*(==|!=|<|>|<=|>=)\s*(0x[0-9a-fA-F]+|\d+)\s*$/);
  if (!m) return false;

  const regName = m[1];
  const op = m[2];
  const literalStr = m[3];

  const regValue = registers[regName];
  if (regValue === undefined) return false;

  const literal = literalStr.startsWith('0x') || literalStr.startsWith('0X')
    ? parseInt(literalStr, 16)
    : parseInt(literalStr, 10);

  if (isNaN(literal)) return false;

  switch (op) {
    case '==': return regValue === literal;
    case '!=': return regValue !== literal;
    case '<': return regValue < literal;
    case '>': return regValue > literal;
    case '<=': return regValue <= literal;
    case '>=': return regValue >= literal;
    default: return false;
  }
}

export class BreakpointManager {
  private codeBreakpoints: CodeBreakpoint[] = [];
  private memoryBreakpoints: MemoryBreakpoint[] = [];
  private bridge: DebugBridge | null;
  readonly el: HTMLDivElement;
  private listEl: HTMLDivElement;
  private addFormEl: HTMLDivElement;
  private registersCache: Record<string, number> = {};

  constructor(bridge?: DebugBridge) {
    this.bridge = bridge ?? null;

    this.el = document.createElement('div');
    this.el.className = 'bp-manager';

    const title = document.createElement('h4');
    title.textContent = 'Breakpoints';
    this.el.appendChild(title);

    this.addFormEl = document.createElement('div');
    this.addFormEl.className = 'bp-add-form';

    const addrInput = document.createElement('input');
    addrInput.type = 'text';
    addrInput.placeholder = 'Address (hex)';
    addrInput.className = 'bp-addr-input';

    const csrInput = document.createElement('input');
    csrInput.type = 'text';
    csrInput.placeholder = 'CSR';
    csrInput.className = 'bp-csr-input';
    csrInput.value = '0';

    const condInput = document.createElement('input');
    condInput.type = 'text';
    condInput.placeholder = 'Condition (optional)';
    condInput.className = 'bp-cond-input';

    const typeSelect = document.createElement('select');
    typeSelect.className = 'bp-type-select';
    const codeOpt = document.createElement('option');
    codeOpt.value = 'code';
    codeOpt.textContent = 'Code BP';
    typeSelect.appendChild(codeOpt);

    const addBtn = document.createElement('button');
    addBtn.textContent = 'Add';
    addBtn.className = 'bp-add-btn';
    addBtn.onclick = () => {
      const type = typeSelect.value;
      const addr = parseInt(addrInput.value.trim(), 16);
      if (isNaN(addr)) return;

      if (type === 'code') {
        const csr = parseInt(csrInput.value.trim(), 10) || 0;
        const cond = condInput.value.trim() || undefined;
        this.addCodeBreakpoint(addr, csr, cond);
      }
      addrInput.value = '';
      condInput.value = '';
    };

    this.addFormEl.appendChild(typeSelect);
    this.addFormEl.appendChild(addrInput);
    this.addFormEl.appendChild(csrInput);
    this.addFormEl.appendChild(condInput);
    this.addFormEl.appendChild(addBtn);
    this.el.appendChild(this.addFormEl);

    this.listEl = document.createElement('div');
    this.listEl.className = 'bp-list';
    this.el.appendChild(this.listEl);
  }

  addCodeBreakpoint(address: number, csr: number, condition?: string): void {
    const existing = this.codeBreakpoints.find(
      bp => bp.address === address && bp.csr === csr,
    );
    if (existing) {
      existing.enabled = true;
      existing.condition = condition;
      return;
    }

    this.codeBreakpoints.push({
      address,
      csr,
      enabled: true,
      condition,
      hitCount: 0,
    });

    if (this.bridge) {
      void this.bridge.addBreakpoint({ address, csr, condition });
    }
  }

  removeCodeBreakpoint(address: number, csr: number): void {
    const idx = this.codeBreakpoints.findIndex(
      bp => bp.address === address && bp.csr === csr,
    );
    if (idx === -1) return;
    this.codeBreakpoints.splice(idx, 1);

    if (this.bridge) {
      void this.bridge.removeBreakpoint(address, csr);
    }
  }

  toggleCodeBreakpoint(address: number, csr: number): void {
    const bp = this.codeBreakpoints.find(
      b => b.address === address && b.csr === csr,
    );
    if (bp) {
      bp.enabled = !bp.enabled;
      if (!bp.enabled && this.bridge) {
        void this.bridge.removeBreakpoint(address, csr);
      }
      if (bp.enabled && this.bridge) {
        void this.bridge.addBreakpoint({ address, csr, condition: bp.condition });
      }
    } else {
      this.addCodeBreakpoint(address, csr);
    }
  }

  getCodeBreakpoints(): CodeBreakpoint[] {
    return [...this.codeBreakpoints];
  }

  addMemoryBreakpoint(mbp: MemoryBreakpoint): void {
    const existing = this.memoryBreakpoints.find(b => b.address === mbp.address && b.type === mbp.type);
    if (existing) {
      existing.enabled = true;
      existing.length = mbp.length;
      return;
    }
    this.memoryBreakpoints.push({ ...mbp });
  }

  removeMemoryBreakpoint(address: number): void {
    const idx = this.memoryBreakpoints.findIndex(b => b.address === address);
    if (idx === -1) return;
    this.memoryBreakpoints.splice(idx, 1);
  }

  getMemoryBreakpoints(): MemoryBreakpoint[] {
    return [...this.memoryBreakpoints];
  }

  checkCodeBreakpoint(address: number, csr: number, registers?: Record<string, number>): CodeBreakpoint | null {
    if (registers) {
      this.registersCache = registers;
    }
    for (const bp of this.codeBreakpoints) {
      if (!bp.enabled) continue;
      if (bp.address !== address || bp.csr !== csr) continue;

      if (bp.condition) {
        const regs = registers ?? this.registersCache;
        if (!parseCondition(bp.condition, regs)) continue;
      }

      bp.hitCount++;
      return bp;
    }
    return null;
  }

  checkMemoryBreakpoint(address: number, type: 'read' | 'write'): MemoryBreakpoint | null {
    for (const bp of this.memoryBreakpoints) {
      if (!bp.enabled) continue;
      if (bp.type !== 'both' && bp.type !== type) continue;
      const bpEnd = bp.address + bp.length;
      if (address >= bp.address && address < bpEnd) {
        bp.hitCount++;
        return bp;
      }
    }
    return null;
  }

  evaluateCondition(condition: string, registers: Record<string, number>): boolean {
    return parseCondition(condition, registers);
  }

  serialize(): string {
    return JSON.stringify({
      code: this.codeBreakpoints,
      memory: this.memoryBreakpoints,
    });
  }

  deserialize(data: string): void {
    try {
      const parsed = JSON.parse(data);
      if (parsed.code && Array.isArray(parsed.code)) {
        this.codeBreakpoints = parsed.code.map((bp: CodeBreakpoint) => ({
          address: bp.address,
          csr: bp.csr,
          enabled: bp.enabled,
          condition: bp.condition,
          hitCount: bp.hitCount ?? 0,
        }));
      }
      if (parsed.memory && Array.isArray(parsed.memory)) {
        this.memoryBreakpoints = parsed.memory.map((bp: MemoryBreakpoint) => ({
          address: bp.address,
          length: bp.length,
          type: bp.type,
          enabled: bp.enabled,
          hitCount: bp.hitCount ?? 0,
        }));
      }
    } catch {
      // ignore parse errors
    }
  }

  render(): HTMLElement {
    this.update();
    return this.el;
  }

  update(): void {
    this.listEl.innerHTML = '';

    const allBreakpoints: Array<{ type: 'code' | 'memory'; data: CodeBreakpoint | MemoryBreakpoint }> = [];

    for (const bp of this.codeBreakpoints) {
      allBreakpoints.push({ type: 'code', data: bp });
    }
    for (const bp of this.memoryBreakpoints) {
      allBreakpoints.push({ type: 'memory', data: bp });
    }

    if (allBreakpoints.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'bp-empty';
      empty.textContent = 'No breakpoints';
      this.listEl.appendChild(empty);
      return;
    }

    for (const { type, data } of allBreakpoints) {
      const row = document.createElement('div');
      row.className = 'bp-item';

      const info = document.createElement('span');
      info.className = 'bp-info';
      if (type === 'code') {
        const cb = data as CodeBreakpoint;
        info.textContent = `Code 0x${cb.address.toString(16).toUpperCase()} CSR:${cb.csr}` +
          (cb.condition ? ` if ${cb.condition}` : '') +
          ` hits:${cb.hitCount}`;
      } else {
        const mb = data as MemoryBreakpoint;
        info.textContent = `Mem ${mb.type} 0x${mb.address.toString(16).toUpperCase()}+${mb.length} hits:${mb.hitCount}`;
      }
      if (!data.enabled) {
        info.style.textDecoration = 'line-through';
      }
      row.appendChild(info);

      const toggleBtn = document.createElement('button');
      toggleBtn.textContent = data.enabled ? 'Disable' : 'Enable';
      toggleBtn.className = 'bp-toggle-btn';
      toggleBtn.onclick = () => {
        data.enabled = !data.enabled;
        this.update();
      };
      row.appendChild(toggleBtn);

      const removeBtn = document.createElement('button');
      removeBtn.textContent = 'Remove';
      removeBtn.className = 'bp-remove-btn';
      removeBtn.onclick = () => {
        if (type === 'code') {
          const cb = data as CodeBreakpoint;
          this.removeCodeBreakpoint(cb.address, cb.csr);
        } else {
          const mb = data as MemoryBreakpoint;
          this.removeMemoryBreakpoint(mb.address);
        }
        this.update();
      };
      row.appendChild(removeBtn);

      this.listEl.appendChild(row);
    }
  }

  destroy(): void {
    if (this.el.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
  }
}
